import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import websocketService from '../services/websocketService';
import SessionConflictListener from '../components/session/SessionConflictListener';
import QueueRealtimeSyncListener from '../components/queue/QueueRealtimeSyncListener';

const WebSocketContext = createContext(undefined);

/**
 * WebSocketContext Provider
 * Manages persistent WebSocket connection for agents
 * Connection persists from login to logout, independent of chat sessions
 */
export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  /**
   * Initialize persistent WebSocket connection for agent
   * This connection is used for:
   * - Receiving chat request notifications
   * - Receiving chat messages (when subscribed to a chat session)
   * - Sending messages to chat sessions
   */
  const connect = useCallback(() => {
    if (websocketService.isAuthFailureTerminal()) {
      setIsConnected(false);
      setConnectionStatus('auth-failed');
      return;
    }

    // Check if already connected
    if (websocketService.getIsConnected()) {
      setIsConnected(true);
      setConnectionStatus('connected');
      return;
    }

    const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
    if (!token) {
      console.warn('No token available for WebSocket connection');
      setConnectionStatus('error');
      return;
    }

    setConnectionStatus('connecting');

    websocketService.connect(
      token,
      () => {
        setIsConnected(true);
        setConnectionStatus('connected');
        // disconnect() clears message handlers; let listeners (e.g. session conflict) re-subscribe
        window.dispatchEvent(new CustomEvent('websocket-connected'));
      },
      (error) => {
        console.error('WebSocket connection error:', error);
        setIsConnected(false);
        if (websocketService.isAuthFailureTerminal()) {
          setConnectionStatus('auth-failed');
        } else if (!websocketService.isShutdownRequested()) {
          setConnectionStatus('error');
        }
        // Reconnect is owned by websocketService (backoff + auth-failure policy).
      }
    );
  }, []);

  /**
   * Disconnect WebSocket connection
   * Should only be called on logout
   */
  const disconnect = useCallback(() => {
    websocketService.disconnect();
    setIsConnected(false);
    setConnectionStatus('disconnected');
  }, []);

  /**
   * Subscribe to a chat session for receiving messages on the single persistent WebSocket.
   * Do not open a second socket with the same userId — the server now tracks multiple
   * sessions per agent, but a duplicate connection was overwriting the notification channel
   * and breaking chat_request delivery after the first chat.
   */
  const subscribeToChatSession = useCallback((chatSessionId, onMessage) => {
    if (!websocketService.getIsConnected()) {
      console.warn('WebSocket not connected. Cannot subscribe to chat session.');
      return () => {}; // Return no-op unsubscribe
    }

    const messageHandler = (message) => {
      let messageData = message;
      if (message.payload) {
        messageData = message.payload;
      }

      const chatId = messageData.chatSessionId || message.chatSessionId;
      if (chatId === chatSessionId) {
        //console.log(`Message received for chat session ${chatSessionId}:`, messageData);
        onMessage(messageData);
      }
    };

    websocketService.subscribe('CHAT_MESSAGE', messageHandler);
    websocketService.subscribe('MESSAGE', messageHandler);

    //console.log(`Subscribed to chat session: ${chatSessionId}`);

    return () => {
      websocketService.unsubscribe('CHAT_MESSAGE', messageHandler);
      websocketService.unsubscribe('MESSAGE', messageHandler);
      //console.log(`Unsubscribed from chat session: ${chatSessionId}`);
    };
  }, []);

  /**
   * Send a message to a chat session via the persistent connection
   */
  const sendChatMessage = useCallback((chatSessionId, content, senderType = 'AGENT', senderName = null, senderId = null) => {
    if (!websocketService.getIsConnected()) {
      console.error('WebSocket not connected. Cannot send message.');
      return false;
    }

    return websocketService.sendChatMessage(chatSessionId, content, senderType, senderName, senderId);
  }, []);

  /**
   * Send typing indicator
   */
  const sendTypingIndicator = useCallback((chatSessionId, senderType = 'AGENT', senderName = null, senderId = null) => {
    if (!websocketService.getIsConnected()) {
      return false;
    }

    return websocketService.sendTypingIndicator(chatSessionId, senderType, senderName, senderId);
  }, []);

  /**
   * Send END_SESSION message (customer-initiated)
   */
  const sendEndSession = useCallback((chatSessionId, senderName = null, senderId = null) => {
    if (!websocketService.getIsConnected()) {
      console.error('WebSocket not connected. Cannot send END_SESSION.');
      return false;
    }

    const message = {
      type: 'END_SESSION',
      payload: {
        chatSessionId: chatSessionId,
        senderType: 'GUEST',
        senderName: senderName || 'Guest',
        senderId: senderId || null,
        timestamp: Date.now(),
      }
    };

    return websocketService.sendMessage(message);
  }, []);

  // Initialize connection when component mounts (if user already has a token, e.g. refresh)
  useEffect(() => {
    const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
    if (token) {
      connect();
    }
  }, [connect]);

  useEffect(() => {
    const onAuthFailure = () => {
      setIsConnected(false);
      setConnectionStatus('auth-failed');
    };
    window.addEventListener('websocket-auth-failure', onAuthFailure);
    return () => window.removeEventListener('websocket-auth-failure', onAuthFailure);
  }, []);

  // Connect as soon as login completes (token was absent on first paint — mount effect skipped)
  useEffect(() => {
    const handleLogin = () => {
      const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
      if (token) {
        connect();
      }
    };

    window.addEventListener('user-logged-in', handleLogin);
    return () => window.removeEventListener('user-logged-in', handleLogin);
  }, [connect]);

  // Listen for logout events to disconnect
  useEffect(() => {
    const handleLogout = () => {
      disconnect();
    };

    // Listen for custom logout event
    window.addEventListener('user-logged-out', handleLogout);

    return () => {
      window.removeEventListener('user-logged-out', handleLogout);
    };
  }, [disconnect]);

  const value = {
    isConnected,
    connectionStatus,
    connect,
    disconnect,
    subscribeToChatSession,
    sendChatMessage,
    sendTypingIndicator,
    sendEndSession,
  };

  return (
    <WebSocketContext.Provider value={value}>
      <SessionConflictListener />
      <QueueRealtimeSyncListener />
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to use WebSocket context
 */
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
